import { useWebsiteSettings } from 'client/store'
import { localeNames } from 'common/constants/localeTypes'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { copyPageLocale } from 'src/editor/api/website-pages-api'
import { ConfirmationDialogWindow } from 'src/editor/shared/ui-lib/modal-window/dialog-window/confirmation-dialog-window'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'

interface AddPageLocaleProps {
  websitePageSetId: number
  copyPagesLocale: PageLocaleEnum | null
  onClose: () => void
}

export const AddPageLocale = ({
  websitePageSetId,
  copyPagesLocale,
  onClose,
}: AddPageLocaleProps) => {
  const { t } = useTranslation()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const pendingCopyLocaleRef = useRef<PageLocaleEnum | null>(null)

  const handleCloseCopyPages = () => {
    pendingCopyLocaleRef.current = null
    onClose()
  }

  const { fetcher: fetcherCopyPages, isLoading: isLoadingCopyPages } =
    useMutationWithValidation({
      requestFunction: copyPageLocale,
      successToastMessage: t(
        'page_settings.locales.copy_pages.copied_successfully',
      ),
      handleSuccess: () => {
        onClose()
      },
      handleFinally: () => {
        pendingCopyLocaleRef.current = null
      },
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
    })

  const handleCopyPages = () => {
    if (!copyPagesLocale || pendingCopyLocaleRef.current === copyPagesLocale) {
      return
    }

    pendingCopyLocaleRef.current = copyPagesLocale
    fetcherCopyPages({
      websitePageSetId,
      locale: copyPagesLocale as PageLocaleEnum,
    })
  }

  return (
    <ConfirmationDialogWindow
      isOpened={!!copyPagesLocale}
      onClose={handleCloseCopyPages}
      title={t(
        'page_settings.locales.copy_pages.confirmation_window.title_icu',
        {
          locale: copyPagesLocale ? t(localeNames[copyPagesLocale]) : '',
        },
      )}
      description={t(
        'page_settings.locales.copy_pages.confirmation_window.description',
      )}
      cancelButtonProps={{
        children: t('global.skip'),
        onClick: handleCloseCopyPages,
      }}
      confirmButtonProps={{
        isLoading: isLoadingCopyPages,
        onClick: handleCopyPages,
        children: t(
          'page_settings.locales.copy_pages.confirmation_window.copy_button',
        ),
      }}
    />
  )
}
